import React, { useState, useEffect } from 'react';
import { 
    Box, 
    Typography, 
    Container, 
    IconButton,
    Paper,
    Tabs,
    Tab,
    useTheme,
    CircularProgress,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Snackbar,
    Alert,
    Tooltip,
    Divider
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import LockIcon from '@mui/icons-material/Lock';
import { useNavigate, useParams } from 'react-router-dom';
import { productionConsentsService, ProductionConsentDetails as ConsentDetailsType } from '../../services/production-consents-service';
import { languageService, Language } from '../../services/language-service';
import { vaultService } from '../../services/vault-service';
import DetailsTab from './components/DetailsTab';
import CollectionPointTab from './components/CollectionPointTab';
import GeolocationTabV2 from './components/GeolocationTabV2';
import PrivacyPolicyTab from './components/PrivacyPolicyTab';
import YourRightsTab from './components/YourRightsTab';
import ContactDpoTab from './components/ContactDpoTab';
import DataElementsTab from './components/DataElementsTab';
import PurposesTab from './components/PurposesTab';
import PreferencesTab from './components/PreferencesTab';

const ProductionConsentDetails: React.FC = () => {
    const theme = useTheme();
    const navigate = useNavigate();
    const { consentId } = useParams<{ consentId: string }>();

    // State management
    const [consentData, setConsentData] = useState<ConsentDetailsType | null>(null);
    const [activeTab, setActiveTab] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [languages, setLanguages] = useState<Language[]>([]);
    
    // Vault popup state
    const [viewValueDialog, setViewValueDialog] = useState(false);
    const [actualValue, setActualValue] = useState<string>('');
    const [maskedToken, setMaskedToken] = useState<string>('');
    const [loadingValue, setLoadingValue] = useState(false);
    const [valueError, setValueError] = useState<string>('');
    const [copySuccess, setCopySuccess] = useState(false);

    // Tab configuration
    const tabs = [
        { label: 'Details', value: 0 },
        { label: 'Collection Point', value: 1 },
        { label: 'Geolocation', value: 2 },
        { label: 'Privacy Policy', value: 3 },
        { label: 'Your Rights', value: 4 },
        { label: 'Contact DPO', value: 5 },
        { label: 'Data Elements', value: 6 },
        { label: 'Purposes', value: 7 },
        { label: 'Preferences', value: 8 }
    ];

    // Load languages on component mount
    useEffect(() => {
        const loadLanguages = async () => {
            try {
                const languageData = await languageService.getSupportedLanguages();
                setLanguages(languageData);
            } catch (error) {
                console.error('Failed to load languages:', error);
            }
        };
        loadLanguages();
    }, []);

    // Fetch consent data
    useEffect(() => {
        const fetchConsentData = async () => {
            if (!consentId) return;
            
            try {
                setLoading(true);
                setError(null);
                const response = await productionConsentsService.getProductionConsentById(parseInt(consentId));
                if (response.success) {
                    setConsentData(response.data);
                } else {
                    setError('Failed to fetch consent details');
                }
            } catch (err) {
                setError(err instanceof Error ? err.message : 'Failed to fetch consent details');
            } finally {
                setLoading(false);
            }
        };

        fetchConsentData();
    }, [consentId]);

    // Get language name from code
    const getLanguageName = (languageCode: string): string => {
        // Hardcode English for 'en' as requested
        if (languageCode.toLowerCase() === 'en') {
            return 'English';
        }
        
        const language = languages.find(lang => 
            lang.code.toLowerCase() === languageCode.toLowerCase()
        );
        
        if (language) {
            if (language.name !== language.localeName) {
                return `${language.name} (${language.localeName})`;
            }
            return language.name;
        }
        
        // Fallback to uppercase code if not found
        return languageCode.toUpperCase();
    };

    const handleBackClick = () => {
        navigate('/workbench/consents');
    };

    const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
        setActiveTab(newValue);
    };

    // Vault handling functions
    const handleViewPIIValues = async (vaultId: number) => {
        if (!vaultId) return;
        
        setViewValueDialog(true);
        setLoadingValue(true);
        setValueError('');
        setActualValue('');
        setMaskedToken('');

        try {
            // Fetch both actual value and masked token in parallel
            const [actualResponse, maskedResponse] = await Promise.allSettled([
                vaultService.getActualValue(vaultId),
                vaultService.getMaskedToken(vaultId)
            ]);

            // Handle actual value response
            if (actualResponse.status === 'fulfilled') {
                setActualValue(actualResponse.value.originalValue);
            } else {
                console.error('Failed to get actual value:', actualResponse.reason);
                // Check if it's a 403 permission error
                if (actualResponse.reason?.response?.status === 403) {
                    setActualValue('Insufficient permissions to view actual value');
                } else {
                    setActualValue('Failed to retrieve actual value');
                }
            }

            // Handle masked token response
            if (maskedResponse.status === 'fulfilled') {
                setMaskedToken(maskedResponse.value.partiallyMaskedToken);
            } else {
                console.error('Failed to get masked token:', maskedResponse.reason);
                // Check if it's a 403 permission error
                if (maskedResponse.reason?.response?.status === 403) {
                    setMaskedToken('Insufficient permissions to view masked token');
                } else {
                    setMaskedToken('Failed to retrieve masked token');
                }
            }

        } catch (error) {
            console.error('Error fetching vault values:', error);
            setValueError('An error occurred while retrieving the data. Please try again.');
        } finally {
            setLoadingValue(false);
        }
    };

    const handleCopyToClipboard = async (value: string) => {
        try {
            await navigator.clipboard.writeText(value);
            setCopySuccess(true);
        } catch (error) {
            console.error('Failed to copy to clipboard:', error);
        }
    };

    const handleCloseDialog = () => {
        setViewValueDialog(false);
        setActualValue('');
        setMaskedToken('');
        setValueError('');
    };

    return (
        <Container maxWidth="xl">
            <Box sx={{ py: 3 }}>
                {/* Header with Back Button */}
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                    <IconButton
                        onClick={handleBackClick}
                        sx={{ 
                            mr: 2, 
                            color: 'primary.main',
                            '&:hover': {
                                bgcolor: 'primary.light',
                                color: 'primary.contrastText'
                            }
                        }}
                    >
                        <ArrowBackIcon sx={{ fontSize: 24 }} />
                    </IconButton>
                    <Box sx={{ flexGrow: 1 }}>
                        <Typography variant="h4" component="h1" gutterBottom sx={{ color: 'text.primary' }}>
                            Consent Details
                        </Typography>
                        <Typography variant="body1" color="text.secondary">
                            Consent ID: #{consentId}
                        </Typography>
                    </Box>
                    {consentData && (
                        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                            <Chip 
                                label={consentData.details.dataPrincipal.identifierValue}
                                variant="outlined"
                                onClick={() => consentData.details.dataPrincipal.vaultId && handleViewPIIValues(consentData.details.dataPrincipal.vaultId)}
                                sx={{ 
                                    cursor: consentData.details.dataPrincipal.vaultId ? 'pointer' : 'default',
                                    borderColor: 'primary.main',
                                    color: 'primary.main',
                                    '&:hover': consentData.details.dataPrincipal.vaultId ? {
                                        bgcolor: 'primary.dark',
                                        color: 'primary.main',
                                        borderColor: 'primary.dark'
                                    } : {}
                                }}
                            />
                            <Chip 
                                label={getLanguageName(consentData.details.consentHeader.languageCode)}
                                color="secondary"
                                variant="outlined"
                            />
                        </Box>
                    )}
                </Box>

                {/* Summary Information */}
                {consentData && (
                    <Paper sx={{ p: 3, mb: 3, bgcolor: 'background.paper' }}>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
                            <Box>
                                <Typography variant="caption" color="text.secondary">Data Principal</Typography>
                                <Typography 
                                    variant="body2" 
                                    sx={{ 
                                        fontWeight: 'medium',
                                        cursor: consentData.details.dataPrincipal.vaultId ? 'pointer' : 'default',
                                        color: consentData.details.dataPrincipal.vaultId ? 'primary.main' : 'text.primary',
                                        '&:hover': consentData.details.dataPrincipal.vaultId ? {
                                            textDecoration: 'underline'
                                        } : {}
                                    }}
                                    onClick={() => consentData.details.dataPrincipal.vaultId && handleViewPIIValues(consentData.details.dataPrincipal.vaultId)}
                                >
                                    {consentData.details.dataPrincipal.identifierValue}
                                </Typography>
                            </Box>
                            <Box>
                                <Typography variant="caption" color="text.secondary">Consent Time</Typography>
                                <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                                    {new Date(consentData.details.consentHeader.consentTimestamp).toLocaleString()}
                                </Typography>
                            </Box>
                            <Box>
                                <Typography variant="caption" color="text.secondary">Collection Point</Typography>
                                <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                                    {consentData.collectionPoint.name}
                                </Typography>
                            </Box>
                            <Box>
                                <Typography variant="caption" color="text.secondary">Client</Typography>
                                <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                                    {consentData.details.client.clientName}
                                </Typography>
                            </Box>
                        </Box>
                    </Paper>
                )}

                {/* Loading State */}
                {loading && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
                        <Box sx={{ textAlign: 'center' }}>
                            <CircularProgress size={40} sx={{ mb: 2 }} />
                            <Typography variant="body2" color="text.secondary">
                                Loading consent details...
                            </Typography>
                        </Box>
                    </Box>
                )}

                {/* Error State */}
                {error && (
                    <Paper sx={{ p: 4, textAlign: 'center', bgcolor: 'error.light' }}>
                        <Typography variant="h6" color="error" gutterBottom>
                            Error Loading Consent Details
                        </Typography>
                        <Typography variant="body1" color="error">
                            {error}
                        </Typography>
                    </Paper>
                )}

                {/* Tab Navigation and Content */}
                {consentData && (
                    <Paper sx={{ bgcolor: 'background.paper' }}>
                        <Tabs 
                            value={activeTab} 
                            onChange={handleTabChange}
                            variant="scrollable"
                            scrollButtons="auto"
                            sx={{ 
                                borderBottom: 1, 
                                borderColor: 'divider',
                                px: 2
                            }}
                        >
                            {tabs.map((tab) => (
                                <Tab 
                                    key={tab.value} 
                                    label={tab.label} 
                                    value={tab.value}
                                    sx={{ minWidth: 'auto' }}
                                />
                            ))}
                        </Tabs>

                        {/* Tab Content */}
                        <Box sx={{ p: 3 }}>
                            {activeTab === 0 && (
                                <DetailsTab consentData={consentData} />
                            )}
                            {activeTab === 1 && (
                                <CollectionPointTab consentData={consentData} />
                            )}
                            {activeTab === 2 && (
                                <GeolocationTabV2 consentData={consentData} />
                            )}
                            {activeTab === 3 && (
                                <PrivacyPolicyTab consentData={consentData} />
                            )}
                            {activeTab === 4 && (
                                <YourRightsTab consentData={consentData} />
                            )}
                            {activeTab === 5 && (
                                <ContactDpoTab consentData={consentData} />
                            )}
                            {activeTab === 6 && (
                                <DataElementsTab consentData={consentData} />
                            )}
                            {activeTab === 7 && (
                                <PurposesTab consentData={consentData} />
                            )}
                            {activeTab === 8 && (
                                <PreferencesTab consentData={consentData} />
                            )}
                        </Box>
                    </Paper>
                )}
            </Box>

            {/* Vault PII Values Dialog */}
            <Dialog 
                open={viewValueDialog} 
                onClose={handleCloseDialog}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <LockIcon color="primary" />
                        <Typography variant="h6">
                            PII Data Details
                        </Typography>
                    </Box>
                </DialogTitle>
                <DialogContent>
                    <Box>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                            Data Principal Identifier
                        </Typography>
                        
                        <Divider sx={{ my: 2 }} />
                        
                        {loadingValue ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 2 }}>
                                <CircularProgress size={20} />
                                <Typography variant="body2" color="text.secondary">
                                    Retrieving PII data...
                                </Typography>
                            </Box>
                        ) : valueError ? (
                            <Box sx={{ py: 2 }}>
                                <Alert severity="error">
                                    {valueError}
                                </Alert>
                            </Box>
                        ) : (actualValue || maskedToken) ? (
                        <Box>
                            {maskedToken && (
                                <Box sx={{ mb: 3 }}>
                                    <Typography variant="body2" color="text.secondary" gutterBottom>
                                        Partially Masked Token:
                                    </Typography>
                                    <Box sx={{ 
                                        p: 2, 
                                        bgcolor: 'background.paper', 
                                        borderRadius: 1, 
                                        border: 1, 
                                        borderColor: maskedToken.includes('permissions') ? 'warning.main' : 'divider',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1
                                    }}>
                                        <Typography 
                                            variant="body1" 
                                            sx={{ 
                                                fontFamily: maskedToken.includes('permissions') ? 'inherit' : 'monospace',
                                                flexGrow: 1,
                                                wordBreak: 'break-all',
                                                color: maskedToken.includes('permissions') ? 'warning.dark' : 'text.primary'
                                            }}
                                        >
                                            {maskedToken}
                                        </Typography>
                                        {!maskedToken.includes('permissions') && (
                                            <Tooltip title="Copy to clipboard">
                                                <IconButton 
                                                    size="small" 
                                                    onClick={() => handleCopyToClipboard(maskedToken)}
                                                    color="primary"
                                                >
                                                    <ContentCopyIcon sx={{ fontSize: 16 }} />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                    </Box>
                                </Box>
                            )}
                            
                            {actualValue && (
                                <Box>
                                    <Typography variant="body2" color="text.secondary" gutterBottom>
                                        Actual Value:
                                    </Typography>
                                    <Box sx={{ 
                                        p: 2, 
                                        borderRadius: 1, 
                                        border: 1, 
                                        borderColor: actualValue.includes('permissions') ? 'warning.main' : 'error.main',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1
                                    }}>
                                        <Typography 
                                            variant="body1" 
                                            sx={{ 
                                                fontFamily: actualValue.includes('permissions') ? 'inherit' : 'monospace',
                                                flexGrow: 1,
                                                wordBreak: 'break-all',
                                                color: actualValue.includes('permissions') ? 'warning.dark' : 'error.dark'
                                            }}
                                        >
                                            {actualValue}
                                        </Typography>
                                        {!actualValue.includes('permissions') && (
                                            <Tooltip title="Copy to clipboard">
                                                <IconButton 
                                                    size="small" 
                                                    onClick={() => handleCopyToClipboard(actualValue)}
                                                    sx={{ 
                                                        color: 'error.dark',
                                                        '&:hover': {
                                                            bgcolor: 'error.main',
                                                            color: 'error.contrastText'
                                                        }
                                                    }}
                                                >
                                                    <ContentCopyIcon sx={{ fontSize: 16 }} />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                    </Box>
                                </Box>
                            )}
                        </Box>
                    ) : null}
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDialog}>
                        Close
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Copy Success Snackbar */}
            <Snackbar
                open={copySuccess}
                autoHideDuration={3000}
                onClose={() => setCopySuccess(false)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert 
                    onClose={() => setCopySuccess(false)} 
                    severity="success" 
                    sx={{ width: '100%' }}
                >
                    Copied to clipboard!
                </Alert>
            </Snackbar>
        </Container>
    );
};

export default ProductionConsentDetails;
